var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import '../../styles/widgets/popup/numberFramesManipulativePopup.scss';
import FooterActionsButtons from './FooterActionsButtons';
import { t } from 'i18next';
import RadioButton from '../../components/RadioButton';
import CustomScrollbar from '../../components/CustomScrollbar';
import { useOnResize } from '../../helper/hooks/onResize';
var FRAME_PRESETS = {
    '5': { rows: 1, cols: 5 },
    '10': { rows: 2, cols: 5 },
    '20': { rows: 2, cols: 10 },
    '100': { rows: 10, cols: 10 },
};
var GRID_SIZE = 12; // 12 x 12 base grid for Custom
// iPad / other touch-primary devices (pattern used elsewhere in this codebase
// to distinguish iPad-with-VoiceOver from desktop). Used here to gate an extra
// tabIndex on the preview grid so iPad VO swipe can reach it, without adding
// an extra Tab stop for desktop keyboard users.
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var NumberFramesManipulativePopup = function (_a) {
    var _b, _c, _d, _e, _f;
    var initialConfig = _a.initialConfig, onSubmit = _a.onSubmit, onCancel = _a.onCancel;
    // Default to 5-frame for new frames. If an existing frame is being edited,
    // its stored frameType is preserved via initialConfig.
    var _g = useState((_b = initialConfig === null || initialConfig === void 0 ? void 0 : initialConfig.frameType) !== null && _b !== void 0 ? _b : '10'), frameType = _g[0], setFrameType = _g[1];
    var frameOrder = ['5', '10', '20', '100', 'custom'];
    var frameLabels = [
        t('text.fiveFrame') || '5-frame',
        t('text.tenFrame') || '10-frame',
        t('text.twentyFrame') || '20-frame',
        t('text.hundredFrame') || '100-frame',
        t('text.custom') || 'Custom',
    ];
    // Screen-reader labels that describe the grid dimensions instead of the
    // visual "N-frame" name. Index matches `frameOrder`. The custom option is
    // intentionally omitted (it's disabled + not tab-reachable below).
    var frameAriaLabels = [
        t('text.frame5RowCol') || '1 row, 5 columns',
        t('text.frame10RowCol') || '2 rows, 5 columns',
        t('text.frame20RowCol') || '2 rows, 10 columns',
        t('text.frame100RowCol') || '10 rows, 10 columns',
    ];
    // For custom selection
    var _h = useState(null), anchorIndex = _h[0], setAnchorIndex = _h[1];
    var _j = useState(null), hoverIndex = _j[0], setHoverIndex = _j[1];
    var _k = useState(null), finalSelection = _k[0], setFinalSelection = _k[1];
    var _l = useState(false), keyboardSelecting = _l[0], setKeyboardSelecting = _l[1];
    var popupRef = useRef(null);
    var gridRef = useRef(null);
    var cells = useMemo(function () { return Array.from({ length: GRID_SIZE * GRID_SIZE }, function (_, i) { return i; }); }, []);
    var getRowCol = function (index) { return ({
        row: Math.floor(index / GRID_SIZE),
        col: index % GRID_SIZE,
    }); };
    var getCellIndex = function (element) {
        var button = element.closest('button[data-cell-index]');
        if (!button)
            return null;
        var indexStr = button.getAttribute('data-cell-index');
        return indexStr ? parseInt(indexStr, 10) : null;
    };
    var selectedRect = useMemo(function () {
        if (frameType !== 'custom' || anchorIndex === null || hoverIndex === null)
            return null;
        var a = getRowCol(anchorIndex);
        var b = getRowCol(hoverIndex);
        var top = Math.min(a.row, b.row);
        var left = Math.min(a.col, b.col);
        var bottom = Math.max(a.row, b.row);
        var right = Math.max(a.col, b.col);
        return {
            rowStart: top,
            rowEnd: bottom,
            colStart: left,
            colEnd: right,
            rows: bottom - top + 1,
            cols: right - left + 1,
        };
    }, [frameType, anchorIndex, hoverIndex]);
    var isCellSelected = function (index) {
        if (frameType !== 'custom')
            return false;
        // Check against finalSelection (for persisted display after release)
        if (finalSelection) {
            var _a = getRowCol(index), row_1 = _a.row, col_1 = _a.col;
            return (row_1 >= finalSelection.rowStart &&
                row_1 <= finalSelection.rowEnd &&
                col_1 >= finalSelection.colStart &&
                col_1 <= finalSelection.colEnd);
        }
        // Check against current selection during drag
        if (!selectedRect)
            return false;
        var _b = getRowCol(index), row = _b.row, col = _b.col;
        return (row >= selectedRect.rowStart &&
            row <= selectedRect.rowEnd &&
            col >= selectedRect.colStart &&
            col <= selectedRect.colEnd);
    };
    // Returns true if cell should be shaded (groups of 5, checkerboard by 5x5 blocks)
    var isCellShaded = function (index, cols) {
        var row = Math.floor(index / cols);
        var col = index % cols;
        var groupRow = Math.floor(row / 5);
        var groupCol = Math.floor(col / 5);
        return (groupRow + groupCol) % 2 === 0;
    };
    // Returns edge classes for cells on the outer border of the selection
    var getCellEdgeClasses = function (index) {
        var rect = finalSelection || selectedRect;
        if (!rect)
            return '';
        var _a = getRowCol(index), row = _a.row, col = _a.col;
        var classes = [];
        if (row === rect.rowStart)
            classes.push('sel-top');
        if (row === rect.rowEnd)
            classes.push('sel-bottom');
        if (col === rect.colStart)
            classes.push('sel-left');
        if (col === rect.colEnd)
            classes.push('sel-right');
        return classes.join(' ');
    };
    // Handle grid events with event delegation
    useEffect(function () {
        if (frameType !== 'custom' || !gridRef.current)
            return;
        var grid = gridRef.current;
        var handleMouseDown = function (e) {
            var index = getCellIndex(e.target);
            if (index === null)
                return;
            // Reset selection on new drag start
            setFinalSelection(null);
            setAnchorIndex(index);
            setHoverIndex(index);
        };
        var handleMouseEnter = function (e) {
            if (anchorIndex === null)
                return;
            var index = getCellIndex(e.target);
            if (index === null)
                return;
            setHoverIndex(index);
        };
        var handleMouseUp = function () {
            // Store complete final selection if exists
            if (selectedRect && frameType === 'custom') {
                setFinalSelection({
                    rowStart: selectedRect.rowStart,
                    rowEnd: selectedRect.rowEnd,
                    colStart: selectedRect.colStart,
                    colEnd: selectedRect.colEnd,
                    rows: selectedRect.rows,
                    cols: selectedRect.cols,
                });
            }
            setAnchorIndex(null);
            setHoverIndex(null);
        };
        var handleTouchStart = function (e) {
            var index = getCellIndex(e.target);
            if (index === null)
                return;
            e.preventDefault();
            // Reset selection on new drag start
            setFinalSelection(null);
            setAnchorIndex(index);
            setHoverIndex(index);
        };
        var handleTouchMove = function (e) {
            if (anchorIndex === null)
                return;
            e.preventDefault();
            var touch = e.touches[0];
            if (!touch)
                return;
            var elementUnderFinger = document.elementFromPoint(touch.clientX, touch.clientY);
            if (!elementUnderFinger)
                return;
            var index = getCellIndex(elementUnderFinger);
            if (index === null)
                return;
            setHoverIndex(index);
        };
        var handleTouchEnd = function () {
            // Store complete final selection if exists
            if (selectedRect && frameType === 'custom') {
                setFinalSelection({
                    rowStart: selectedRect.rowStart,
                    rowEnd: selectedRect.rowEnd,
                    colStart: selectedRect.colStart,
                    colEnd: selectedRect.colEnd,
                    rows: selectedRect.rows,
                    cols: selectedRect.cols,
                });
            }
            setAnchorIndex(null);
            setHoverIndex(null);
        };
        var moveFocus = function (row, col) {
            var _a;
            if (row < 0 || col < 0 || row >= GRID_SIZE || col >= GRID_SIZE)
                return;
            var nextIndex = row * GRID_SIZE + col;
            var nextButton = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button[data-cell-index=\"".concat(nextIndex, "\"]"));
            if (nextButton) {
                nextButton.focus();
                if (keyboardSelecting) {
                    setHoverIndex(nextIndex);
                }
            }
        };
        var handleKeyDown = function (e) {
            if (frameType !== 'custom')
                return;
            var button = e.target.closest('button[data-cell-index]');
            if (!button)
                return;
            var index = getCellIndex(button);
            if (index === null)
                return;
            var _a = getRowCol(index), row = _a.row, col = _a.col;
            switch (e.key) {
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    if (!keyboardSelecting) {
                        // Start selection
                        setFinalSelection(null);
                        setAnchorIndex(index);
                        setHoverIndex(index);
                        setKeyboardSelecting(true);
                    }
                    else {
                        // Finish selection
                        if (selectedRect) {
                            setFinalSelection(__assign({}, selectedRect));
                        }
                        setAnchorIndex(null);
                        setHoverIndex(null);
                        setKeyboardSelecting(false);
                    }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    moveFocus(row, col + 1);
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    moveFocus(row, col - 1);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    moveFocus(row + 1, col);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    moveFocus(row - 1, col);
                    break;
            }
        };
        // Attach event listeners to grid
        grid.addEventListener('mousedown', handleMouseDown);
        grid.addEventListener('mouseenter', handleMouseEnter, true); // capture phase for mouseenter
        grid.addEventListener('touchstart', handleTouchStart, { passive: false });
        grid.addEventListener('touchmove', handleTouchMove, { passive: false });
        grid.addEventListener('keydown', handleKeyDown);
        // Attach document listeners for up/end events
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchend', handleTouchEnd);
        return function () {
            grid.removeEventListener('mousedown', handleMouseDown);
            grid.removeEventListener('mouseenter', handleMouseEnter, true);
            grid.removeEventListener('touchstart', handleTouchStart);
            grid.removeEventListener('touchmove', handleTouchMove);
            grid.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [frameType, anchorIndex, selectedRect]);
    var computeFinalConfig = function () {
        var _a, _b;
        if (frameType !== 'custom') {
            var preset = FRAME_PRESETS[frameType];
            return { frameType: frameType, rows: preset.rows, cols: preset.cols };
        }
        // Use stored final selection if available, otherwise use current selection
        if (finalSelection) {
            return { frameType: 'custom', rows: finalSelection.rows, cols: finalSelection.cols };
        }
        var rows = (_a = selectedRect === null || selectedRect === void 0 ? void 0 : selectedRect.rows) !== null && _a !== void 0 ? _a : GRID_SIZE;
        var cols = (_b = selectedRect === null || selectedRect === void 0 ? void 0 : selectedRect.cols) !== null && _b !== void 0 ? _b : GRID_SIZE;
        return { frameType: 'custom', rows: rows, cols: cols };
    };
    var handleDone = function () {
        var cfg = computeFinalConfig();
        // Do not allow empty custom selection → require at least 1x1
        if (cfg.frameType === 'custom' &&
            !finalSelection &&
            (!selectedRect || cfg.rows === 0 || cfg.cols === 0)) {
            return;
        }
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(cfg);
    };
    var disableDone = frameType === 'custom' &&
        !finalSelection &&
        (!selectedRect || selectedRect.rows === 0 || selectedRect.cols === 0);
    // Get visible rows and columns based on frame type
    var getVisibleDimensions = function () {
        if (frameType === 'custom')
            return { rows: GRID_SIZE, cols: GRID_SIZE };
        var preset = FRAME_PRESETS[frameType];
        return { rows: preset.rows, cols: preset.cols };
    };
    var visibleDims = getVisibleDimensions();
    var visibleCellCount = visibleDims.rows * visibleDims.cols;
    var gridArea = useRef(null);
    var _m = useState(1), previewScale = _m[0], setPreviewScale = _m[1];
    var UpdatePos = useCallback(function () {
        var _a, _b, _c;
        var wrapper = popupRef.current; // .numberFramesPopupWrapper — the TRUE visible viewport
        var area = gridArea.current;
        var popup = gridRef.current;
        if (!wrapper || !area || !popup)
            return;
        // Reset before measuring so we get the grid's natural size. Clear the
        // margin compensation too so it doesn't skew the measurement.
        popup.style.transform = 'scale(1)';
        popup.style.transformOrigin = 'top center';
        popup.style.marginBottom = '0px';
        // Available height = wrapper height − everything that's NOT the grid
        // (radio selector + bottom panel + custom hint + gridArea vertical padding).
        var selector = wrapper.querySelector('.frameSelectorContainer');
        var bottomPanel = wrapper.querySelector('.bottomPanel');
        var hint = wrapper.querySelector('.customFrameHint');
        var areaCs = window.getComputedStyle(area);
        var areaPadX = parseFloat(areaCs.paddingLeft) + parseFloat(areaCs.paddingRight);
        var areaPadY = parseFloat(areaCs.paddingTop) + parseFloat(areaCs.paddingBottom);
        var viewportH = wrapper.clientHeight;
        var availH = viewportH -
            ((_a = selector === null || selector === void 0 ? void 0 : selector.offsetHeight) !== null && _a !== void 0 ? _a : 0) -
            ((_b = bottomPanel === null || bottomPanel === void 0 ? void 0 : bottomPanel.offsetHeight) !== null && _b !== void 0 ? _b : 0) -
            ((_c = hint === null || hint === void 0 ? void 0 : hint.offsetHeight) !== null && _c !== void 0 ? _c : 0) -
            areaPadY;
        var availW = wrapper.clientWidth - areaPadX;
        var popupWidth = popup.offsetWidth;
        var popupHeight = popup.offsetHeight;
        if (!popupWidth || !popupHeight || availW <= 0 || availH <= 0)
            return;
        // Fit both dimensions; cap at 1 so we never enlarge beyond natural size.
        var widthScale = availW / popupWidth;
        var heightScale = availH / popupHeight;
        var scaleVal = Math.min(widthScale, heightScale, 1);
        popup.style.transform = "scale(".concat(scaleVal, ")");
        // Collapse the layout box to match the visual size so the frame selector
        // below sits directly under the scaled grid instead of leaving dead space.
        // (CSS transforms don't affect layout, so we reclaim the "lost" pixels
        // with a negative bottom margin.)
        popup.style.marginBottom = "".concat(-(popupHeight * (1 - scaleVal)), "px");
        setPreviewScale(scaleVal);
    }, []);
    var handleResize = useCallback(function () {
        UpdatePos();
    }, [UpdatePos, frameType, finalSelection]);
    useOnResize(handleResize);
    useEffect(function () {
        UpdatePos();
    }, [UpdatePos, frameType, finalSelection]);
    // Hide the Custom radio option from every assistive-tech navigation model —
    // desktop JAWS/NVDA/VoiceOver virtual cursor, Mac VO swipe, iPad VO swipe,
    // and plain keyboard. Combined with `nonTabbableOptions={[4]}` on the
    // RadioButton, keyboard users (with or without an AT) can't reach or
    // activate Custom via Tab, arrow keys, Enter, or Space. Mouse-click on the
    // label still works — `aria-hidden` and `tabindex=-1` don't block pointer
    // events, only AT focus/announcement.
    useEffect(function () {
        var root = popupRef.current;
        if (!root)
            return;
        var customItem = root.querySelectorAll('.customRadioItem')[4];
        if (!customItem)
            return;
        customItem.setAttribute('aria-hidden', 'true');
        var input = customItem.querySelector('input');
        if (input) {
            input.setAttribute('tabindex', '-1');
            input.setAttribute('aria-hidden', 'true');
            input.setAttribute('aria-disabled', 'true');
        }
    }, []);
    // On open, land keyboard / SR focus on the FIRST radio (5-frame) instead of
    // the currently-selected one (10-frame) so the tab / reading order starts
    // at the top of the radio group as users expect.
    //
    // iPad / iOS VoiceOver notes:
    //  - The radio input is visually clipped (see radioButton.scss). Safari is
    //    stricter than Chrome about programmatic focus on clipped inputs.
    //  - VO's cursor lags the React commit; 50ms isn't enough.
    //  - VO responds to `scrollIntoView` more reliably than bare `.focus()`.
    // Combining a longer delay + rAF + scrollIntoView moves the VO cursor on
    // iPad without affecting desktop behavior.
    useEffect(function () {
        var timer = setTimeout(function () {
            requestAnimationFrame(function () {
                var _a;
                var firstRadio = (_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input[type="radio"]');
                if (!firstRadio)
                    return;
                firstRadio.scrollIntoView({ block: 'nearest' });
                firstRadio.focus();
            });
        }, 300);
        return function () { return clearTimeout(timer); };
    }, []);
    return (_jsxs("div", { className: "numberFramesPopupWrapper", ref: popupRef, children: [_jsx("div", { className: "scrollbarHolder", children: _jsx(CustomScrollbar, { direction: "vertical", children: _jsxs("div", { className: "numberFramesPopup", children: [_jsxs("div", { className: "gridArea", ref: gridArea, children: [frameType === 'custom' && (_jsx("p", { className: "customFrameHint", children: (_c = t('text.numberFramesCustomHint')) !== null && _c !== void 0 ? _c : 'Make a frame by dragging' })), _jsx("div", { ref: gridRef, role: "grid", id: "grid", "aria-rowcount": visibleDims.rows, "aria-colcount": visibleDims.cols, className: "grid", style: {
                                            gridTemplateColumns: "repeat(".concat(visibleDims.cols, ", 1fr)"),
                                            gridTemplateRows: "repeat(".concat(visibleDims.rows, ", 1fr)"),
                                        }, "aria-label": IS_TOUCH_DEVICE
                                            ? t('text.numberFramePreviewRowsCols', {
                                                rows: visibleDims.rows,
                                                cols: visibleDims.cols,
                                            })
                                            : (_d = t('text.numberFramePreview')) !== null && _d !== void 0 ? _d : 'Number frame preview', 
                                        // iPad VoiceOver swipe skips this block otherwise: every cell
                                        // is disabled in preset modes (5/10/20/100) so none are swipe
                                        // targets, and role="grid" alone doesn't make the wrapper one.
                                        // tabIndex={0} promotes it into the a11y tree as a discoverable
                                        // element. Gated on touch-primary so desktop keyboard users
                                        // don't get an extra Tab stop.
                                        tabIndex: IS_TOUCH_DEVICE ? 0 : undefined, children: cells.slice(0, visibleCellCount).map(function (index) { return (_jsx("button", { role: "gridcell", type: "button", "data-cell-index": index, className: "gridCell ".concat((frameType === '20' || frameType === '100') &&
                                                isCellShaded(index, visibleDims.cols)
                                                ? ''
                                                : '', " ").concat(frameType === 'custom' && isCellSelected(index)
                                                ? "selected ".concat(getCellEdgeClasses(index))
                                                : ''), "aria-pressed": frameType === 'custom' && isCellSelected(index), disabled: frameType !== 'custom', title: frameType === 'custom' ? "Cell ".concat(index + 1) : 'Preset frame', "aria-selected": isCellSelected(index), "aria-label": "Row ".concat(getRowCol(index).row + 1, ", Column ").concat(getRowCol(index).col + 1) }, index)); }) })] }), _jsx("div", { className: "frameSelectorContainer", children: _jsxs("fieldset", { className: "frameSelectorFieldset", children: [_jsx("legend", { className: "srOnly", children: (_e = t('text.frames')) !== null && _e !== void 0 ? _e : 'Frames' }), _jsx("div", { className: "frameSelectorLabel", "aria-hidden": true, children: (_f = t('text.frames')) !== null && _f !== void 0 ? _f : 'Frames' }), _jsx(RadioButton, { id: "numberFrames-frameType", options: frameLabels, selected: frameOrder.indexOf(frameType), onChange: function (idx) {
                                                if (idx < 0)
                                                    return;
                                                var newFrameType = frameOrder[idx];
                                                setFrameType(newFrameType);
                                                // Clear custom selection when switching to preset
                                                if (newFrameType !== 'custom') {
                                                    setFinalSelection(null);
                                                    setAnchorIndex(null);
                                                    setHoverIndex(null);
                                                }
                                            }, className: "frameSelectorRadioGroup", name: "numberFrames-frameType", 
                                            // SR reads row/col dimensions instead of the visual label.
                                            ariaLabels: frameAriaLabels, useAriaLabelFromOutside: true, 
                                            // Custom (index 4) stays mouse-clickable but is removed from
                                            // Tab + arrow-key navigation so keyboard users can't reach or
                                            // accidentally hover over it.
                                            nonTabbableOptions: [4] })] }) })] }) }) }), _jsx("div", { className: "bottomPanel", children: _jsx(FooterActionsButtons, { disableDoneBtn: disableDone, onActionBtnClick: function (type) {
                        if (type === 'done')
                            handleDone();
                        else
                            onCancel === null || onCancel === void 0 ? void 0 : onCancel();
                    } }) })] }));
};
export default NumberFramesManipulativePopup;
